// src/screens/StudyScreen.js
//
// Distinct from sleep mode on purpose: different sound profile (lo-fi/white
// noise instead of rain+birds+melody) and a focus/break timer instead of a
// sleep timer. Also pulls in anything the user tagged "Study" in their
// personal library.

import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, ImageBackground } from 'react-native';
import SoundTile from '../components/SoundTile';
import { STUDY_SOUNDS } from '../data/sounds';
import { useLibrary } from '../context/LibraryContext';
import { AudioEngine } from '../audio/AudioEngine';
import { Colors, Radius, Typography, Fonts, useFontScale } from '../theme';

const FOCUS_MIN = 25;
const BREAK_MIN = 5;

// Distinct color for break vs focus phase
const PHASE_COLORS = { focus: Colors.accent, break: '#62c4a4' };

export default function StudyScreen() {
  const { tracksInCategory, reimportTrack, markTrackMissing } = useLibrary();
  const studyTracks = tracksInCategory('Study');
  const fontScale = useFontScale();

  const [phase, setPhase] = useState('idle'); // idle | focus | break
  const [reimportingId, setReimportingId] = useState(null);
  const [remainingSec, setRemainingSec] = useState(null);

  const startCycle = (minutes, nextPhase) => {
    AudioEngine.startSleepTimer(
      minutes,
      1,
      (secLeft) => setRemainingSec(secLeft),
      () => {
        if (nextPhase === 'focus') startCycle(BREAK_MIN, 'break');
        else startCycle(FOCUS_MIN, 'focus');
      }
    );
    setPhase(nextPhase);
  };

  const start = () => startCycle(FOCUS_MIN, 'focus');
  const stop = () => {
    AudioEngine.cancelSleepTimer();
    AudioEngine.stopAll({ fadeMs: 1500 });
    setPhase('idle');
    setRemainingSec(null);
  };

  const formatTime = (sec) => {
    if (sec == null) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const phaseLabel = phase === 'focus' ? '🎯 Focus' : '☕ Break';
  const phaseColor = PHASE_COLORS[phase] ?? Colors.accent;

  return (
    <ImageBackground
      source={require('../../assets/backgrounds/bg_study.jpg')}
      style={styles.screen}
      imageStyle={styles.bgImage}
    >
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content}>
      <Text style={styles.subheader}>
        Focus sound + a 25/5 work-break cycle. Fades between phases so
        switching doesn't jolt you out of flow.
      </Text>

      <View style={styles.cycleCard}>
        {phase === 'idle' ? (
          <Pressable style={[styles.actionButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]} onPress={start}>
            <Text style={styles.actionText}>Start focus session</Text>
          </Pressable>
        ) : (
          <>
            <Text style={[styles.phaseChip, { color: phaseColor, fontSize: Math.round(15 * fontScale) }]} allowFontScaling={false}>{phaseLabel}</Text>
            <Text style={[styles.timerDisplay, { fontSize: Math.round(40 * fontScale) }]} allowFontScaling={false}>{formatTime(remainingSec)}</Text>
            <Pressable
              style={[styles.actionButton, styles.stopButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]}
              onPress={stop}
            >
              <Text style={[styles.actionText, styles.stopText]}>End session</Text>
            </Pressable>
          </>
        )}
      </View>

      <Text style={styles.sectionTitle}>Focus sounds</Text>
      {STUDY_SOUNDS.map((s) => (
        <SoundTile key={s.id} id={s.id} name={s.name} uri={s.uri} icon={s.icon} />
      ))}

      {studyTracks.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>From your "Study" library</Text>
          {studyTracks.map((t) => (
            <SoundTile
              key={t.id}
              id={`library-${t.id}`}
              name={t.name}
              uri={t.uri}
              artworkUri={t.artworkUri}
              icon="musical-notes"
              needsReimport={!!t.needsReimport}
              importing={reimportingId === t.id}
              onReimport={t.needsReimport ? async () => {
                // Show the per-tile spinner for the entire duration of the
                // picker interaction and file copy. The finally block guarantees
                // the spinner is always cleared — whether the user cancels the
                // picker (reimportTrack returns null), reimportTrack throws, or
                // the import succeeds. Without finally, a throw or cancellation
                // would leave reimportingId set and the tile permanently disabled.
                setReimportingId(t.id);
                try { return await reimportTrack(t.id); } finally { setReimportingId(null); }
              } : undefined}
              onMissing={!t.needsReimport ? () => markTrackMissing(t.id) : undefined}
            />
          ))}
        </>
      )}
    </ScrollView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  screen:    { flex: 1, backgroundColor: Colors.bg },
  bgImage:   { opacity: 0.18 },
  content:   { padding: 16, paddingBottom: 32 },
  subheader: { color: Colors.textSecondary, ...Typography.caption, marginBottom: 18 },

  cycleCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    padding: 20,
    marginBottom: 20,
    alignItems: 'center',
  },
  phaseChip: {
    fontFamily: Fonts.bold,
    // fontSize applied inline via useFontScale() to update mid-session
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  timerDisplay: {
    color: Colors.textBright,
    // fontSize applied inline via useFontScale() to update mid-session
    fontFamily: Fonts.light,
    letterSpacing: 2,
    marginBottom: 16,
  },
  actionButton: {
    backgroundColor: Colors.accent,
    borderRadius: Radius.md,
    paddingVertical: 14,
    paddingHorizontal: 32,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  stopButton:  { backgroundColor: Colors.danger },
  actionText:  { color: Colors.textOnAccent, ...Typography.button },
  stopText:    { color: Colors.dangerText },

  sectionTitle: {
    color: Colors.textSecondary,
    ...Typography.small,
    fontFamily: Fonts.semiBold,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 4,
  },
});
